import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { motion } from 'framer-motion';

function Header({ variant = 'default', heroSectionRef = null, footerSectionRef = null }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [heroScrolled, setHeroScrolled] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const navRef = useRef(null);
  const location = useLocation();

  const scrolled = heroScrolled || footerVisible;

  const navigationItems = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about-us' },
    { label: 'Services', path: '/services' },
    { label: 'Technologies', path: '/technologies' },
  ];

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!heroSectionRef?.current && !footerSectionRef?.current) return;

    const observers = [];

    if (heroSectionRef?.current) {
      const heroObserver = new IntersectionObserver(
        ([entry]) => {
          setHeroScrolled(!entry.isIntersecting);
        },
        {
          threshold: 0,
          rootMargin: '-68px 0px 0px 0px',
        }
      );

      heroObserver.observe(heroSectionRef.current);
      observers.push({ observer: heroObserver, element: heroSectionRef.current });
    }

    if (footerSectionRef?.current) {
      const footerObserver = new IntersectionObserver(
        ([entry]) => {
          setFooterVisible(entry.isIntersecting);
        },
        {
          threshold: 0,
          rootMargin: '0px',
        }
      );

      footerObserver.observe(footerSectionRef.current);
      observers.push({ observer: footerObserver, element: footerSectionRef.current });
    }

    return () => {
      observers.forEach(({ observer, element }) => {
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [heroSectionRef, footerSectionRef]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => {
      updateIndicator();
    }, 50);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const updateIndicator = () => {
    if (!navRef.current) return;

    const activeLink = navRef.current.querySelector('[data-active="true"]');
    if (activeLink) {
      const navContainer = navRef.current.getBoundingClientRect();
      const linkRect = activeLink.getBoundingClientRect();
      const offsetLeft = linkRect.left - navContainer.left;

      setIndicatorStyle({
        left: offsetLeft,
        width: linkRect.width,
      });
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 transition-all duration-500 ${isMenuOpen ? 'z-[70]' : 'z-50'} ${scrolled
          ? 'bg-white backdrop-blur-xl shadow-lg border-b border-gray-100'
          : variant === 'transparent'
            ? 'bg-gradient-to-b from-black/30 via-black/10 to-transparent backdrop-blur-sm'
            : 'bg-white shadow-sm'
          }`}
      >
        <div className="container mx-auto py-2 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
          <div className="flex h-[68px] 3xl:h-[80px] items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="flex-shrink-0 transition-transform hover:scale-105 active:scale-95"
            >
              <div
                className={`transition-all duration-300 mt-2 ${variant === 'transparent' && !scrolled
                  ? 'drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]'
                  : ''
                  }`}
              >
                <img
                  src="/assets/images/header.png"
                  alt="Zovox Technologies"
                  className="h-10 sm:h-12 3xl:h-16 w-auto"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center flex-1 justify-center">
              <div ref={navRef} className="relative flex items-center gap-x-2 3xl:gap-x-6">
                {navigationItems.map((item) => (
                  <motion.div
                    key={item.path}
                    className="relative"
                    whileHover="hover"
                    initial="rest"
                    animate="rest"
                  >
                    <Link
                      to={item.path}
                      data-active={isActive(item.path)}
                      className={`relative px-4 py-2 3xl:px-6 3xl:py-3 text-[15px] 3xl:text-lg font-semibold transition-all duration-200 block whitespace-nowrap ${isActive(item.path)
                        ? variant === 'transparent' && !scrolled
                          ? 'text-white drop-shadow-lg'
                          : 'text-blue-600'
                        : variant === 'transparent' && !scrolled
                          ? 'text-white/95 hover:text-white drop-shadow-md hover:drop-shadow-lg'
                          : 'text-slate-700 hover:text-slate-900'
                        }`}
                    >
                      {item.label}
                    </Link>

                    {!isActive(item.path) && (
                      <motion.span
                        className={`absolute bottom-[-10px] left-0 right-0 h-[3px] pointer-events-none ${variant === 'transparent' && !scrolled
                          ? 'bg-white shadow-lg shadow-white/50'
                          : 'bg-blue-600'
                          }`}
                        variants={{
                          rest: { scaleX: 0 },
                          hover: { scaleX: 1 },
                        }}
                        transition={{
                          duration: 0.3,
                          ease: 'easeOut',
                        }}
                        style={{
                          transformOrigin: 'left',
                        }}
                      />
                    )}
                  </motion.div>
                ))}

                <motion.span
                  className={`absolute bottom-[-10px] left-0 h-[3px] pointer-events-none ${variant === 'transparent' && !scrolled
                    ? 'bg-white shadow-lg shadow-white/50'
                    : 'bg-blue-600'
                    }`}
                  initial={false}
                  animate={{
                    left: indicatorStyle.left,
                    width: indicatorStyle.width,
                  }}
                  transition={{
                    type: 'spring',
                    stiffness: 380,
                    damping: 30,
                  }}
                />
              </div>
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:block flex-shrink-0">
              <Link to="/contact-us">
                <button
                  className={`group relative inline-flex items-center justify-center gap-2 rounded-lg px-6 py-2.5 3xl:px-8 3xl:py-4 text-[15px] 3xl:text-lg font-semibold transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98] whitespace-nowrap ${variant === 'transparent' && !scrolled
                    ? 'bg-white text-blue-600 shadow-xl shadow-white/20 hover:shadow-2xl hover:shadow-white/30 hover:bg-blue-50'
                    : 'bg-blue-600 text-white shadow-lg shadow-blue-600/25 hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-600/35'
                    }`}
                >
                  Contact Us
                  <svg
                    className="h-4 w-4 3xl:h-5 3xl:w-5 transition-transform group-hover:translate-x-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-all duration-200 relative z-[60] ${variant === 'transparent' && !scrolled && !isMenuOpen
                ? 'text-white hover:bg-white/20 backdrop-blur-sm drop-shadow-lg'
                : 'text-slate-700 hover:bg-slate-100'
                }`}
              aria-label="Toggle menu"
            >
              <Icon name={isMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 top-0 z-[60] lg:hidden">
          {/* Header spacer to prevent content from going under the header */}
          <div className="h-[68px] bg-white" />

          <div
            className="absolute inset-0 top-[68px] bg-slate-900/20 backdrop-blur-sm"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="relative bg-white shadow-2xl h-full">
            <div className="px-4 py-6" style={{ maxWidth: '100%' }}>
              <div className="flex flex-col space-y-1">
                {navigationItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative rounded-lg px-4 py-3 text-base font-medium transition-colors ${isActive(item.path)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-700 hover:bg-slate-50'
                      }`}
                  >
                    {item.label}
                    {isActive(item.path) && (
                      <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-blue-600 rounded-r-full" />
                    )}
                  </Link>
                ))}
                <div className="pt-3">
                  <Link to="/contact-us" className="block">
                    <button className="w-full rounded-lg bg-blue-600 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-700 active:scale-[0.98]">
                      Contact Us
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Header;
